import { makeApiRequest, objectRequest } from "@web/lib/data";
import { cache } from "react";
export interface BlogRef {
  id: string;
  keywords: string;
  title: string;
}

export interface Blog extends BlogRef {
  content: string;
  metadata?: Partial<BlogPreviewMetadata>;
  created_at: string;
}

export interface BlogPreviewMetadata {
  image: string;
  title: string;
  description: string;
  date: string;
  author?: string;
  authorImage?: string;
  authorRole?: string;
}

export const previewLength = 60;
export const pageSize = 15;

function stripPreviewMarkup(value: string) {
  return value
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/<\/?[^>]+(>|$)/g, " ")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s{0,3}>\s?/gm, "")
    .replace(/^\s{0,3}[-+*]\s+/gm, "")
    .replace(/[`*_~]/g, "")
    .replace(/\r?\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function clampPreviewText(value: string) {
  if (value.length <= previewLength) {
    return value;
  }

  return value.slice(0, previewLength - 3).trimEnd() + "...";
}

function createPreviewDescription(value: string | undefined) {
  return clampPreviewText(stripPreviewMarkup(value ?? ""));
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return "Unknown error";
}

function logBlogError(scope: string, error: unknown) {
  console.error(`[blogs] ${scope}: ${getErrorMessage(error)}`);
}

function normalizeBlogSlug(value: string) {
  return value
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function createSlugForPost(title: string) {
  return normalizeBlogSlug(title);
}

export async function findBySlug(slug: string) {
  const normalizedSlug = normalizeBlogSlug(slug);

  for (const item of await getBlogRefs()) {
    if (normalizedSlug === createSlugForPost(item.title)) {
      return item;
    }
  }
}

function getPostImage(content: string): string | undefined {
  const regex = /!\[[^\]]*\]\((.*?)\s*("(?:.*[^"])")?\s*\)/g;
  const res = regex.exec(content);
  if (res?.length ?? 0 > 1) {
    return res?.[1];
  }
  return undefined;
}

export async function getRecent(page: number, size: number = pageSize) {
  const allBlogs = await getBlogs();
  const blogs = allBlogs.slice(page * size, page * size + size);
  return {
    items: await Promise.all(blogs.map(getPreviewMetadata)),
    hasNextPage: page * size + size < allBlogs.length,
  };
}

export async function getPreviewMetadata(
  blog: Blog,
): Promise<BlogPreviewMetadata> {
  const baseMetadata = {
    title: blog.title,
    image:
      getPostImage(blog.content) ??
      "https://cdn.britannica.com/51/190751-050-147B93F7/soccer-ball-goal.jpg",
    description: createPreviewDescription(blog.content),
    date: new Date(blog.created_at).toDateString(),
  };

  if (blog.metadata) {
    return {
      ...baseMetadata,
      ...blog.metadata,
      description: createPreviewDescription(
        typeof blog.metadata.description === "string"
          ? blog.metadata.description
          : baseMetadata.description,
      ),
    };
  }

  return baseMetadata;
}

export const getBlogFromRef = cache(async (ref: BlogRef) => {
  const { content } = await makeApiRequest("GET", `blog/${ref.id}`).then(
    objectRequest,
  );

  if (
    content.content.startsWith("<metadata>") &&
    content.content.includes("</metadata>")
  ) {
    const split = content.content.split("</metadata>");
    return {
      ...ref,
      ...content,
      content: split[1],
      metadata: JSON.parse(split[0].slice("<metadata>".length)),
    } as Blog;
  }
  return { ...ref, ...content } as Blog;
});

export async function getBlogRefs() {
  try {
    const { content: blogRefs } = await makeApiRequest("GET", "blog/list").then(
      objectRequest,
    );

    return blogRefs as BlogRef[];
  } catch (error) {
    logBlogError("Failed to fetch blog list", error);
    return [];
  }
}
export async function getBlogs() {
  const blogRefs = await getBlogRefs();
  const results = await Promise.allSettled(blogRefs.map(getBlogFromRef));

  return results.flatMap((result) => {
    if (result.status === "fulfilled") {
      return [result.value];
    }

    logBlogError("Failed to fetch blog entry", result.reason);
    return [];
  });
}
